import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import styles from './Navbar.module.scss';
import { landingRoute } from '~/utils/constants/routes';
import { LanguageEnum } from '~/utils/enum/base';
import { signInURL, signUpURL } from '~/utils/constants/env';

const cx = classNames.bind(styles);

const Navbar: React.FC = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t, i18n } = useTranslation();
  const location = useLocation();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    return localStorage.getItem('theme') !== 'light';
  });
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleToggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const handleToggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => !prev);
  }, []);

  const handleToggleLanguage = useCallback(() => {
    const nextLang = i18n.language === LanguageEnum.VI ? LanguageEnum.EN : LanguageEnum.VI;
    i18n.changeLanguage(nextLang);
  }, [i18n]);
  //#endregion Handle Function

  const navLinks = [
    { label: t('nav.home'), path: landingRoute.home },
    { label: t('nav.features'), path: landingRoute.features },
    { label: t('nav.pricing'), path: landingRoute.pricing },
    { label: t('nav.about'), path: landingRoute.about },
  ];

  const isVi = i18n.language === LanguageEnum.VI;

  return (
    <nav
      className={cx(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled ? 'bg-lumi-dark-200/90 backdrop-blur-md border-b border-white/10' : 'bg-transparent'
      )}
    >
      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex items-center justify-between h-16'>
          <Link to={landingRoute.home} className='flex items-center gap-2 flex-shrink-0'>
            <img src={require('~/assets/images/logo.svg').default} alt='Lumi' className='w-8 h-8 rounded-lg' />
            <span className='text-white font-bold text-xl tracking-tight'>{t('nav.logo')}</span>
          </Link>

          <div className='hidden md:flex items-center gap-6'>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={cx(
                  'text-base font-medium transition-colors duration-200',
                  location.pathname === link.path ? 'text-lumi-400' : 'text-white/70 hover:text-white'
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className='hidden md:flex items-center gap-3'>
            <button
              onClick={handleToggleLanguage}
              title='Switch language'
              className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-xs font-semibold transition-all duration-200'
            >
              <span className='text-sm'>{isVi ? '\u{1F1FB}\u{1F1F3}' : '\u{1F1FA}\u{1F1F8}'}</span>
              <span>{isVi ? 'VI' : 'EN'}</span>
            </button>

            <button
              onClick={handleToggleDarkMode}
              title={isDarkMode ? 'Switch to Light mode' : 'Switch to Dark mode'}
              className='w-9 h-9 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 flex items-center justify-center transition-all duration-200'
            >
              {isDarkMode ? (
                <svg className='w-4 h-4 text-yellow-300' fill='currentColor' viewBox='0 0 24 24'>
                  <path d='M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.166 17.834a.75.75 0 00-1.06 1.06l1.59 1.591a.75.75 0 001.061-1.06l-1.59-1.591zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.166 6.166a.75.75 0 001.06 1.06l1.591-1.59a.75.75 0 10-1.06-1.061L6.166 6.166z' />
                </svg>
              ) : (
                <svg className='w-4 h-4 text-white/70' fill='currentColor' viewBox='0 0 24 24'>
                  <path fillRule='evenodd' d='M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z' clipRule='evenodd' />
                </svg>
              )}
            </button>

            <div className='w-px h-5 bg-white/20' />

            <a
              href={signInURL}
              className='text-sm font-medium text-white/70 hover:text-white transition-colors duration-200'
            >
              {t('common.signIn')}
            </a>
            <a
              href={signUpURL}
              className='px-4 py-2 rounded-lg bg-lumi-600 hover:bg-lumi-500 text-white text-sm font-medium transition-all duration-200 hover:shadow-lg hover:shadow-lumi-600/30'
            >
              {t('common.getStarted')}
            </a>
          </div>

          <div className='md:hidden flex items-center gap-2'>
            <button
              onClick={handleToggleLanguage}
              className='flex items-center px-2 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-xs font-semibold'
            >
              <span>{isVi ? '\u{1F1FB}\u{1F1F3}' : '\u{1F1FA}\u{1F1F8}'}</span>
            </button>

            <button
              onClick={handleToggleDarkMode}
              className='w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center'
            >
              {isDarkMode ? (
                <svg className='w-4 h-4 text-yellow-300' fill='currentColor' viewBox='0 0 24 24'>
                  <path d='M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.166 17.834a.75.75 0 00-1.06 1.06l1.59 1.591a.75.75 0 001.061-1.06l-1.59-1.591zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.166 6.166a.75.75 0 001.06 1.06l1.591-1.59a.75.75 0 10-1.06-1.061L6.166 6.166z' />
                </svg>
              ) : (
                <svg className='w-4 h-4 text-white/70' fill='currentColor' viewBox='0 0 24 24'>
                  <path fillRule='evenodd' d='M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z' clipRule='evenodd' />
                </svg>
              )}
            </button>

            <button
              onClick={handleToggleMobileMenu}
              className='flex flex-col gap-1.5 p-2 rounded-lg hover:bg-white/10 transition-colors'
              aria-label='Toggle menu'
            >
              <span className={cx('block w-5 h-0.5 bg-white transition-all duration-300', { 'rotate-45 translate-y-2': isMobileMenuOpen })} />
              <span className={cx('block w-5 h-0.5 bg-white transition-all duration-300', { 'opacity-0': isMobileMenuOpen })} />
              <span className={cx('block w-5 h-0.5 bg-white transition-all duration-300', { '-rotate-45 -translate-y-2': isMobileMenuOpen })} />
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className='md:hidden bg-lumi-dark-200/95 backdrop-blur-md border-t border-white/10 px-4 py-4 flex flex-col gap-4'>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cx(
                'text-sm font-medium py-2 transition-colors duration-200',
                location.pathname === link.path ? 'text-lumi-400' : 'text-white/70 hover:text-white'
              )}
            >
              {link.label}
            </Link>
          ))}
          <div className='flex flex-col gap-3 pt-2 border-t border-white/10'>
            <a
              href={signInURL}
              className='text-sm font-medium text-white/70 hover:text-white transition-colors py-2'
            >
              {t('common.signIn')}
            </a>
            <a
              href={signUpURL}
              className='px-4 py-2 rounded-lg bg-lumi-600 hover:bg-lumi-500 text-white text-sm font-medium text-center transition-all duration-200'
            >
              {t('common.getStarted')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;